// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/GestaoLotes.tsx
//  Gestão de lotes de produtos: listagem, criação, edição e exclusão.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ProdutoSearchCombobox } from "@/components/ProdutoSearchCombobox";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Package } from "lucide-react";

const PAGE_SIZES = [10, 20, 50] as const;
import { toast } from "sonner";
import { useLotes } from "@/features/estoque/hooks/useLotes";
import type { LoteResumo, CriarLoteRequest, AtualizarLoteRequest } from "@/features/estoque/types";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtQtd = (v?: number) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString("pt-BR") : "—";

const toInputDate = (s?: string) => (s ? s.slice(0, 10) : "");

function statusVariant(status: string): "default" | "destructive" | "secondary" | "outline" {
  if (status === "vencido")  return "destructive";
  if (status === "vencendo") return "secondary";
  return "outline";
}

function statusLabel(status: string) {
  if (status === "vencido")  return "Vencido";
  if (status === "vencendo") return "Vencendo";
  return "Normal";
}

// ── Dialog: Criar Lote ────────────────────────────────────────────────────────

interface CriarDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSave: (req: CriarLoteRequest) => void;
  saving: boolean;
}

function CriarDialog({ open, onOpenChange, onSave, saving }: CriarDialogProps) {
  const [form, setForm] = useState<CriarLoteRequest>({ produtoId: 0 });

  const set = (key: keyof CriarLoteRequest, value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.produtoId) { toast.error("Selecione o produto."); return; }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo Lote</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Produto *</Label>
              <ProdutoSearchCombobox
                value={form.produtoId || undefined}
                onChange={(pid) => set("produtoId", pid)}
                placeholder="Buscar produto..."
              />
            </div>
            <div className="space-y-1">
              <Label>Nº do Lote</Label>
              <Input
                maxLength={20}
                placeholder="Impresso na embalagem"
                value={form.numeroLote ?? ""}
                onChange={(e) => set("numeroLote", e.target.value || undefined)}
              />
            </div>
            <div className="space-y-1">
              <Label>Quantidade do Lote</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={form.quantidadeLote ?? ""}
                onChange={(e) => set("quantidadeLote", e.target.value ? Number(e.target.value) : undefined)}
              />
            </div>
            <div className="space-y-1">
              <Label>Saldo Inicial</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={form.saldoLote ?? ""}
                onChange={(e) => set("saldoLote", e.target.value ? Number(e.target.value) : undefined)}
              />
            </div>
            <div className="space-y-1">
              <Label>Data de Fabricação</Label>
              <Input
                type="date"
                value={form.dtFabricacao ?? ""}
                onChange={(e) => set("dtFabricacao", e.target.value || undefined)}
              />
            </div>
            <div className="space-y-1">
              <Label>Data de Validade</Label>
              <Input
                type="date"
                value={form.dtValidade ?? ""}
                onChange={(e) => set("dtValidade", e.target.value || undefined)}
              />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Preço Máximo (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={form.precoMaximo ?? ""}
                onChange={(e) => set("precoMaximo", e.target.value ? Number(e.target.value) : undefined)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Salvando…" : "Criar Lote"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Dialog: Editar Lote ───────────────────────────────────────────────────────

interface EditarDialogProps {
  lote: LoteResumo | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSave: (id: number, req: AtualizarLoteRequest) => void;
  saving: boolean;
}

function EditarDialog({ lote, open, onOpenChange, onSave, saving }: EditarDialogProps) {
  const [form, setForm] = useState<AtualizarLoteRequest>({});

  // Sincroniza form quando o lote muda
  const initForm = (l: LoteResumo) => ({
    numeroLote:     l.numeroLote ?? "",
    quantidadeLote: l.quantidadeLote,
    saldoLote:      l.saldoLote,
    dtFabricacao:   l.dtFabricacao ? toInputDate(l.dtFabricacao) : undefined,
    dtValidade:     l.dtValidade   ? toInputDate(l.dtValidade)   : undefined,
    precoMaximo:    l.precoMaximo,
  });

  const set = (key: keyof AtualizarLoteRequest, value: unknown) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!lote) return;
    onSave(lote.id, form);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v && lote) setForm(initForm(lote));
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Lote {lote?.numeroLote || ""}</DialogTitle>
        </DialogHeader>
        {lote && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">{lote.produtoDescricao}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2">
                <Label>Nº do Lote</Label>
                <Input
                  maxLength={20}
                  placeholder="Impresso na embalagem"
                  value={form.numeroLote ?? ""}
                  onChange={(e) => set("numeroLote", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Quantidade do Lote</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.quantidadeLote ?? ""}
                  onChange={(e) => set("quantidadeLote", e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
              <div className="space-y-1">
                <Label>Saldo do Lote</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.saldoLote ?? ""}
                  onChange={(e) => set("saldoLote", e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
              <div className="space-y-1">
                <Label>Data de Fabricação</Label>
                <Input
                  type="date"
                  value={form.dtFabricacao ?? ""}
                  onChange={(e) => set("dtFabricacao", e.target.value || undefined)}
                />
              </div>
              <div className="space-y-1">
                <Label>Data de Validade</Label>
                <Input
                  type="date"
                  value={form.dtValidade ?? ""}
                  onChange={(e) => set("dtValidade", e.target.value || undefined)}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Preço Máximo (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.precoMaximo ?? ""}
                  onChange={(e) => set("precoMaximo", e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página Principal
// ══════════════════════════════════════════════════════════════════════════════

export default function GestaoLotes() {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.AJUSTE_SALDO, "incluir");
  const podeAlterar = temPermissao(FORMULARIOS.AJUSTE_SALDO, "editar");
  const podeExcluir = temPermissao(FORMULARIOS.AJUSTE_SALDO, "excluir");

  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [statusFiltro, setStatusFiltro]   = useState<string>("todos");
  const [produtoFiltro, setProdutoFiltro] = useState<number | undefined>(undefined);
  const [buscaInput, setBuscaInput]       = useState("");
  const [venceEmInput, setVenceEmInput]   = useState("");
  const [somenteComSaldo, setSomenteComSaldo] = useState(false);
  const [isAddOpen, setIsAddOpen]     = useState(false);
  const [editLote, setEditLote]       = useState<LoteResumo | null>(null);
  const [deleteLote, setDeleteLote]   = useState<LoteResumo | null>(null);

  const busca   = useDebounce(buscaInput.trim(), 350);
  const venceEm = useDebounce(venceEmInput, 350);

  // Qualquer filtro novo volta para a página 1.
  useEffect(() => { setPagina(1); }, [busca, venceEm, produtoFiltro, statusFiltro, somenteComSaldo]);

  const { query, criar, atualizar, excluir } = useLotes({
    pagina,
    tamanhoPagina,
    status: statusFiltro === "todos" ? undefined : statusFiltro,
    produtoId: produtoFiltro,
    busca: busca || undefined,
    venceEmDias: venceEm !== "" ? Number(venceEm) : undefined,
    comSaldo: somenteComSaldo || undefined,
  });

  const { data, isLoading, isFetching } = query;
  const total       = data?.total       ?? 0;
  const totalPaginas = data?.totalPaginas ?? 1;
  const firstItem   = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem    = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  const handleCriar = (req: CriarLoteRequest) => {
    criar.mutate(req, {
      onSuccess: () => { toast.success("Lote criado com sucesso!"); setIsAddOpen(false); },
      onError:   () => toast.error("Erro ao criar lote."),
    });
  };

  const handleAtualizar = (id: number, req: AtualizarLoteRequest) => {
    atualizar.mutate({ id, req }, {
      onSuccess: () => { toast.success("Lote atualizado!"); setEditLote(null); },
      onError:   () => toast.error("Erro ao atualizar lote."),
    });
  };

  const handleExcluir = (lote: LoteResumo) => {
    excluir.mutate(lote.id, {
      onSuccess: () => { toast.success("Lote excluído."); setDeleteLote(null); },
      onError:   () => toast.error("Não foi possível excluir o lote. Verifique se há saldo positivo."),
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Gestão de Lotes</h1>
          <p className="text-muted-foreground mt-1">
            Controle de lotes de produtos com validade e saldo
          </p>
        </div>
        {podeIncluir && (
          <Button onClick={() => setIsAddOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Novo Lote
          </Button>
        )}
      </div>

      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center gap-4 pb-3">
          <CardTitle className="flex items-center gap-2 flex-1">
            <Package className="h-5 w-5 text-primary" />
            Lotes Cadastrados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="space-y-1 w-72">
              <Label className="text-xs">Produto</Label>
              <div className="flex gap-1">
                <ProdutoSearchCombobox
                  value={produtoFiltro}
                  onChange={(pid) => setProdutoFiltro(pid || undefined)}
                  placeholder="Todos os produtos"
                />
                {produtoFiltro && (
                  <Button variant="ghost" size="sm" onClick={() => setProdutoFiltro(undefined)}>Limpar</Button>
                )}
              </div>
            </div>
            <div className="space-y-1 w-56">
              <Label className="text-xs">Nº do lote / descrição</Label>
              <Input value={buscaInput} onChange={(e) => setBuscaInput(e.target.value)} placeholder="Buscar..." />
            </div>
            <div className="space-y-1 w-36">
              <Label className="text-xs">Vence em até (dias)</Label>
              <Input type="number" min={0} value={venceEmInput} onChange={(e) => setVenceEmInput(e.target.value)} />
            </div>
            <div className="space-y-1 w-40">
              <Label className="text-xs">Situação</Label>
              <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="vencendo">Vencendo</SelectItem>
                  <SelectItem value="vencido">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm pb-2">
              <input type="checkbox" checked={somenteComSaldo} onChange={(e) => setSomenteComSaldo(e.target.checked)} />
              Só com saldo
            </label>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Nº Lote</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Qtd Lote</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead>Fabricação</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead className="text-right">Preço Máx.</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : !data?.itens.length
                  ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                          Nenhum lote encontrado.
                        </TableCell>
                      </TableRow>
                    )
                  : data.itens.map((lote) => (
                      <TableRow key={lote.id}>
                        <TableCell className="font-mono text-sm">{lote.numeroLote || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="font-medium">{lote.produtoDescricao}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtQtd(lote.quantidadeLote)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{fmtQtd(lote.saldoLote)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(lote.dtFabricacao)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(lote.dtValidade)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {lote.precoMaximo != null
                            ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(lote.precoMaximo)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(lote.status)}>
                            {statusLabel(lote.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {podeAlterar && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => setEditLote(lote)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {podeExcluir && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => setDeleteLote(lote)}
                                disabled={(lote.saldoLote ?? 0) > 0}
                                title={(lote.saldoLote ?? 0) > 0 ? "Lote com saldo — não pode ser excluído" : "Excluir lote"}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CriarDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSave={handleCriar}
        saving={criar.isPending}
      />

      <EditarDialog
        lote={editLote}
        open={editLote !== null}
        onOpenChange={(v) => { if (!v) setEditLote(null); }}
        onSave={handleAtualizar}
        saving={atualizar.isPending}
      />

      <AlertDialog open={deleteLote !== null} onOpenChange={(v) => { if (!v) setDeleteLote(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Lote {deleteLote?.numeroLote || ""}</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir o lote do produto{" "}
              <strong>{deleteLote?.produtoDescricao}</strong>. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteLote && handleExcluir(deleteLote)}
              disabled={excluir.isPending}
            >
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
